import {
  deployConfig,
  type ApiOriginBrand,
  type ApiService,
  type DeployConfig,
  type DeployMode,
  type TestEnv,
} from './deployConfig';

type BuildDeployConfig = {
  deployMode?: string;
  testEnv?: string;
  apiOrigin?: string;
  scrmPrefix?: string;
  guidePrefix?: string;
  appTitle?: string;
  themeColor?: string;
  faviconUrl?: string;
  defaultAvatarUrl?: string;
};

declare const __BUILD_DEPLOY_CONFIG__: BuildDeployConfig;

const buildDeployConfig =
  typeof __BUILD_DEPLOY_CONFIG__ === 'undefined'
    ? {}
    : __BUILD_DEPLOY_CONFIG__;

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const normalizePath = (value?: string): string => {
  if (!value) return '';
  const normalized = value.replace(/^\/+|\/+$/g, '');
  return normalized ? `/${normalized}` : '';
};

const normalizeRequestPath = (value: string): string => {
  if (!value) return '';
  const [, pathname = '', suffix = ''] = value.match(/^([^?#]*)(.*)$/) || [];
  return `${normalizePath(pathname)}${suffix}`;
};

const joinUrl = (base: string, path: string): string =>
  `${trimTrailingSlash(base)}${normalizeRequestPath(path)}`;

const isDeployMode = (value?: string): value is DeployMode =>
  value === 'test' || value === 'prod';

const isTestEnv = (value?: string): value is TestEnv =>
  ['test1', 'test2', 'test3', 'test4', 'test5', 'test6'].includes(value || '');

const resolveDeployMode = (): DeployMode =>
  isDeployMode(buildDeployConfig.deployMode)
    ? buildDeployConfig.deployMode
    : deployConfig.deployMode;

const resolveTestEnv = (): TestEnv =>
  isTestEnv(buildDeployConfig.testEnv)
    ? buildDeployConfig.testEnv
    : deployConfig.testEnv;

const hasBuildDeployOverride =
  buildDeployConfig.deployMode !== undefined ||
  buildDeployConfig.testEnv !== undefined ||
  buildDeployConfig.apiOrigin !== undefined;

export type { ApiOriginBrand, ApiService, DeployConfig, DeployMode, TestEnv };

export const DEPLOY_MODE = resolveDeployMode();

export const TEST_ENV = resolveTestEnv();

export const IS_PROD_DEPLOY = DEPLOY_MODE === 'prod';

export const APP_TITLE = buildDeployConfig.appTitle || deployConfig.appTitle;

export const APP_SLOGAN = deployConfig.appSlogan;

export const API_ORIGIN_BRAND = deployConfig.apiOriginBrand;

export const API_ORIGIN = trimTrailingSlash(
  buildDeployConfig.apiOrigin ??
    deployConfig.apiOrigins[API_ORIGIN_BRAND]?.[DEPLOY_MODE] ??
    ''
);

export const BASE_API_URL = IS_PROD_DEPLOY
  ? API_ORIGIN
  : joinUrl(API_ORIGIN, TEST_ENV);

export const API_SERVICES: Record<ApiService, string> = {
  scrm: normalizePath(
    buildDeployConfig.scrmPrefix ?? deployConfig.apiServices.scrm
  ),
  guide: normalizePath(
    buildDeployConfig.guidePrefix ?? deployConfig.apiServices.guide
  ),
};

export const APP_FAVICON_URL =
  buildDeployConfig.faviconUrl || deployConfig.faviconUrl || './favicon.ico';

export const DEFAULT_AVATAR_URL =
  buildDeployConfig.defaultAvatarUrl ||
  deployConfig.defaultAvatarUrl ||
  APP_FAVICON_URL ||
  './favicon.ico';

export const THEME_COLOR =
  buildDeployConfig.themeColor || deployConfig.themeColor;


export const CURRENT_ENV = IS_PROD_DEPLOY ? 'prod' : TEST_ENV;

export const getApiServicePrefix = (service: ApiService): string =>
  API_SERVICES[service];

export const getServiceBaseUrl = (service: ApiService): string =>
  joinUrl(BASE_API_URL, getApiServicePrefix(service));

export const getApiUrl = (service: ApiService, path: string): string => {
  if (/^(https?:)?\/\//.test(path)) return path;
  return joinUrl(getServiceBaseUrl(service), path);
};
